import React from "react";
import { Panel, Kicker, DataTable } from "../ciWidgets";

const INTENSITY = { low: ["w-1/4", "text-emerald-300"], medium: ["w-2/4", "text-amber-300"], high: ["w-full", "text-red-300"] };

export const CompetitionTab = ({ report }) => {
  const comp = report.competitive || {};
  const swot = comp.swot || {};
  const moat = comp.moat || {};
  const peers = report.peers || [];

  return (
    <div className="space-y-6" data-testid="ci-tab-competition">
      {(comp.peer_matrix || []).length > 0 && (
        <Panel className="p-7">
          <Kicker className="mb-4">Peer comparison matrix</Kicker>
          <DataTable
            testId="ci-peer-matrix-table"
            headers={["Company", "Revenue", "Growth", "Margin", "Market position", "Notes"]}
            rows={comp.peer_matrix.map((r, i) => [
              <span className={i === 0 ? "font-bold text-teal-300" : "font-medium text-white"}>{r.company}</span>,
              r.revenue, r.growth, r.margin, r.position,
              <span className="text-[12px]">{r.notes}</span>,
            ])}
          />
        </Panel>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-7">
          <Kicker className="mb-4">SWOT analysis</Kicker>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Strengths", swot.strengths, "border-emerald-400/25 text-emerald-300"],
              ["Weaknesses", swot.weaknesses, "border-red-400/25 text-red-300"],
              ["Opportunities", swot.opportunities, "border-teal-400/25 text-teal-300"],
              ["Threats", swot.threats, "border-amber-400/25 text-amber-300"],
            ].map(([t, items, cls]) => (
              <div key={t} className={`rounded-lg border bg-[#060d18] p-4 ${cls.split(" ")[0]}`}>
                <div className={`text-[11px] tracking-[0.18em] uppercase font-bold mb-2.5 ${cls.split(" ")[1]}`}>{t}</div>
                <ul className="space-y-1.5">
                  {(items || []).map((x, i) => (
                    <li key={i} className="text-[12px] text-white/65 leading-relaxed">· {x}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Panel>

        <div className="space-y-6">
          <Panel className="p-7">
            <Kicker className="mb-4">Porter's Five Forces</Kicker>
            <div className="space-y-3.5">
              {(comp.five_forces || []).map((f, i) => {
                const [w, cls] = INTENSITY[f.intensity] || INTENSITY.medium;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="text-white/75 font-medium">{f.force}</span>
                      <span className={`font-bold uppercase text-[10px] tracking-[0.12em] ${cls}`}>{f.intensity}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden mb-1">
                      <div className={`h-full rounded-full ${f.intensity === "high" ? "bg-red-400/70" : f.intensity === "low" ? "bg-emerald-400/70" : "bg-amber-400/70"} ${w}`} />
                    </div>
                    <div className="text-[11px] text-white/45 leading-relaxed">{f.rationale}</div>
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel className="p-7">
            <Kicker className="mb-3">Moat assessment</Kicker>
            <div className="flex items-center gap-3 mb-3">
              <span className={`text-lg font-serif uppercase ${moat.rating === "wide" ? "text-emerald-300" : moat.rating === "narrow" ? "text-amber-300" : "text-red-300"}`}>
                {moat.rating || "—"}
              </span>
              <span className="text-[12px] text-white/50">{moat.durability}</span>
            </div>
            <ul className="space-y-1.5">
              {(moat.sources || []).map((x, i) => (
                <li key={i} className="text-[13px] text-white/65">· {x}</li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>

      {(comp.white_space || []).length > 0 && (
        <Panel className="p-7">
          <Kicker className="mb-4">White-space opportunities</Kicker>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {comp.white_space.map((x, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-[#060d18] px-4 py-3.5 text-[13px] text-white/70 leading-relaxed">
                {x}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {peers.length > 0 && (
        <Panel className="p-7">
          <Kicker className="mb-4">Approved peer set</Kicker>
          <DataTable
            testId="ci-approved-peers-table"
            headers={["Peer", "Benchmark group", "Reason for selection", "Material differences", "Data availability", "Confidence"]}
            rows={peers.map((p) => [
              <span className="font-medium text-white">{p.name}</span>,
              <span className="text-teal-300 text-[12px]">{(p.category || "").replace(/_/g, " ")}</span>,
              <span className="text-[12px]">{p.reason}</span>,
              <span className="text-[12px]">{p.differences}</span>,
              p.data_availability, p.confidence,
            ])}
          />
        </Panel>
      )}
    </div>
  );
};
